
/**
 * Meta API Authentication Utilities
 */
import { supabase } from "@/integrations/supabase/client";

/**
 * Validate token has required permissions
 */
export const validateToken = async (token: string): Promise<boolean> => {
  try {
    // Call the edge function to validate token permissions
    const { data, error } = await supabase.functions.invoke('meta-ads-api', {
      body: { 
        action: 'validateToken', 
        accessToken: token 
      }
    });
    
    if (error) {
      console.error('Error validating token:', error);
      return false;
    }
    
    return data?.isValid === true;
  } catch (error) {
    console.error('Failed to validate token:', error);
    return false;
  }
};

/**
 * Get auth URL for Meta connection
 */
export const getMetaAuthUrl = async (): Promise<string> => {
  try {
    const { data, error } = await supabase.functions.invoke('meta-ads-api', {
      body: { action: 'getAuthUrl' }
    });
    
    if (error) {
      console.error('Error invoking edge function:', error);
      throw new Error(`Edge Function Fehler: ${error.message}`);
    }
    
    if (!data?.authUrl) {
      throw new Error('No auth URL received');
    }
    
    return data.authUrl;
  } catch (error: any) {
    console.error('Failed to get Meta auth URL:', error);
    throw error;
  }
};

/**
 * Exchange code for token
 */
export const exchangeCodeForToken = async (code: string): Promise<string> => {
  try {
    // Use a hardcoded redirect URI that matches exactly what's registered in the Meta app
    const redirectUri = "https://app.hoss-immo.de/meta-auth";
    console.log("Exchanging code for token with redirect URI:", redirectUri);
    
    const { data, error } = await supabase.functions.invoke('meta-ads-api', {
      body: { 
        action: 'exchangeCodeForToken', 
        code, 
        redirectUri 
      }
    });
    
    if (error) {
      console.error('Error invoking edge function:', error);
      throw new Error(`Edge Function Fehler: ${error.message}`);
    }
    
    if (!data?.accessToken) {
      console.error('No access token received in response:', data);
      throw new Error('Kein Zugriffstoken erhalten. Bitte versuchen Sie es erneut.');
    }
    
    return data.accessToken;
  } catch (error: any) {
    console.error('Failed to exchange code for token:', error);
    throw error;
  }
};

/**
 * Initiate OAuth flow for Meta connection
 * This opens a popup window to the Meta authorization page
 */
export const initiateOAuthFlow = () => {
  try {
    // Adjust these variables to match your Meta App configuration
    const appId = '1558110261688463'; // Your Meta App ID 
    const redirectUri = encodeURIComponent('https://app.hoss-immo.de/meta-auth');
    const scope = encodeURIComponent('ads_management,ads_read');
    const state = generateRandomState();
    
    // Save state to localStorage to verify on callback
    localStorage.setItem('meta_auth_state', state);
    
    // Construct the authorization URL
    const authUrl = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${appId}&redirect_uri=${redirectUri}&state=${state}&scope=${scope}`;
    
    // Open the popup window
    window.open(authUrl, 'Meta Auth', 'width=800,height=600');
  } catch (error) {
    console.error("Error initiating Meta OAuth flow:", error);
    throw new Error("Fehler beim Starten des Meta-Logins");
  }
};

/**
 * Generate a random state parameter for CSRF protection
 */
const generateRandomState = () => {
  return Math.random().toString(36).substring(2, 15);
};
